// Single source of truth for "can this tenant send an interest request for this
// flat?".
//
// Every rule below pairs ONE field the owner fills in while adding a property
// (PropertyFormModal) with ONE field the tenant fills in while registering /
// editing their profile (TenantRegisteration, TenantProfileEdit):
//
//   owner  allowedFor        <->  tenant  tenantType
//   owner  petAllowed        <->  tenant  hasPets / pets[]
//   owner  parkingAvailable  <->  tenant  hasVehicle / vehicles[]
//   owner  personAllowed     <->  tenant  totalFamilyMembers
//   owner  is_rent           <->  (nothing — the flat is simply gone)
//
// The listing itself always stays visible; only the request action is gated, so
// a tenant can still browse a property they are not eligible for and read the
// exact reason on hover.

// A hard rule blocks the request button. A soft rule only warns — the tenant can
// still send the request. Flip a flag here and every screen follows, because
// every screen reads its state from this one function.
const HARD_RULES = {
  rented: true,
  // Server re-checks this on every /flat-interest/send call and refuses the
  // request outright (FLAT_NOT_LIVE) if it fails — this mirrors that here so
  // the button never promises a request the backend will reject.
  notLive: true,
  tenantType: true,
  pets: true,
  // Owner explicitly said "no parking" and the tenant's profile carries a
  // vehicle — treated as a hard mismatch, same as pets.
  parking: true,
  // Owner's registered occupancy limit is smaller than the tenant's household.
  occupancy: true,
};

export const isOn = (value) =>
  value === true || value === "true" || value === 1 || value === "1";

const isKnown = (value) =>
  value !== undefined && value !== null && value !== "";

// Only an *explicit* "no" from the owner blocks. A property saved before these
// fields existed has `undefined` there, and must never be treated as a refusal.
const isExplicitNo = (value) => isKnown(value) && !isOn(value);

export const normalizeTenantType = (value) => {
  const type = String(value || "").toLowerCase().trim();
  if (type.startsWith("bachelor")) return "bachelor";
  if (type.startsWith("famil")) return "family";
  return "";
};

export const normalizeAllowedFor = (value) => {
  const allowed = String(value || "").toLowerCase().trim();
  if (allowed === "both" || allowed === "any") return "both";
  if (allowed.startsWith("bachelor")) return "bachelor";
  if (allowed.startsWith("famil")) return "family";
  return "";
};

const TYPE_LABEL = { family: "Family", bachelor: "Bachelor" };

export const tenantTypeLabel = (value) =>
  TYPE_LABEL[normalizeTenantType(value)] || "";

export const allowedForLabel = (value) => {
  const allowed = normalizeAllowedFor(value);
  if (allowed === "both") return "Family & Bachelor";
  return TYPE_LABEL[allowed] || "";
};

// Tenant may carry either the boolean flag or the array (profile edit keeps both
// in sync, registration writes both) — count from whichever is present.
export const petCount = (profile = {}) => {
  const pets = Array.isArray(profile.pets) ? profile.pets : [];
  const counted = pets.reduce(
    (total, pet) => total + (Number(pet?.count) || 1),
    0
  );
  if (counted > 0) return counted;
  return isOn(profile.hasPets) ? 1 : 0;
};

export const vehicleCount = (profile = {}) => {
  const vehicles = Array.isArray(profile.vehicles) ? profile.vehicles : [];
  if (vehicles.length > 0) return vehicles.length;
  return isOn(profile.hasVehicle) ? 1 : 0;
};

const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

/**
 * @param {object} flat    the property as the owner saved it
 * @param {object} profile the logged-in tenant's profile
 * @returns {{
 *   canRequest: boolean,
 *   blockers: Array<{key,title,reason,fix}>,
 *   warnings: Array<{key,title,reason,fix}>,
 *   matches:  Array<{key,title}>,
 *   primaryReason: string,
 *   tooltip: string,
 *   shortLabel: string,
 *   hasProfile: boolean
 * }}
 */
export function evaluateTenantEligibility(flat, profile) {
  const blockers = [];
  const warnings = [];
  const matches = [];

  const push = (rule) => {
    (HARD_RULES[rule.key] ? blockers : warnings).push(rule);
  };

  if (!flat) {
    return {
      canRequest: false,
      blockers,
      warnings,
      matches,
      primaryReason: "",
      tooltip: "",
      shortLabel: "",
      hasProfile: false,
    };
  }

  const tenantProfile = profile || {};
  // Without a profile there is nothing to compare against — never block on data
  // we do not have; the availability rule below still applies.
  const hasProfile = Boolean(
    normalizeTenantType(tenantProfile.tenantType) ||
      tenantProfile.totalFamilyMembers ||
      Array.isArray(tenantProfile.pets) ||
      Array.isArray(tenantProfile.vehicles) ||
      isKnown(tenantProfile.hasPets) ||
      isKnown(tenantProfile.hasVehicle)
  );

  // 1. Availability — nothing else matters once the flat is taken.
  if (Number(flat.is_rent) === 1) {
    push({
      key: "rented",
      title: "Already rented",
      reason: "This property is already rented out, so it cannot take new requests.",
      fix: "Browse other available properties.",
    });
  }

  // 1b. Approval status — any edit to the property (even just raising the
  // owner's occupancy limit) resets it to "pending admin review" on the
  // server. A tenant who still has the old listing open would otherwise see
  // every other check pass and reach a button that looks enabled, only for
  // the request to be refused server-side with no visible reason.
  if (isKnown(flat.is_approved) && !isOn(flat.is_approved)) {
    push({
      key: "notLive",
      title: "Pending re-approval",
      reason:
        "The owner recently updated this property, so it has gone back to the admin for review and cannot accept new requests until it is approved again.",
      fix: "Try again once the property is re-approved, or ask the owner to check its approval status.",
    });
  } else if (isOn(flat.is_rejected)) {
    push({
      key: "notLive",
      title: "Not approved",
      reason: "This property was rejected during admin review and is not currently live.",
      fix: "Browse other approved properties.",
    });
  }

  // 2. Family / Bachelor — owner's allowedFor vs tenant's tenantType.
  const allowedFor = normalizeAllowedFor(flat.allowedFor);
  const tenantType = normalizeTenantType(tenantProfile.tenantType);
  if (allowedFor && allowedFor !== "both" && tenantType) {
    if (allowedFor !== tenantType) {
      push({
        key: "tenantType",
        title: `${allowedForLabel(flat.allowedFor)} only`,
        reason: `Owner is renting this property to ${allowedForLabel(
          flat.allowedFor
        ).toLowerCase()} tenants only, and your profile is registered as ${
          TYPE_LABEL[tenantType]
        }.`,
        fix: `Look for ${TYPE_LABEL[tenantType]} or "Family & Bachelor" properties.`,
      });
    } else {
      matches.push({
        key: "tenantType",
        title: `Open to ${TYPE_LABEL[tenantType]} tenants`,
      });
    }
  }

  // 3. Pets — owner's petAllowed vs tenant's pets.
  const pets = petCount(tenantProfile);
  if (pets > 0) {
    if (isExplicitNo(flat.petAllowed)) {
      push({
        key: "pets",
        title: "Pets not allowed",
        reason: `Owner does not allow pets in this property, and your profile lists ${plural(
          pets,
          "pet"
        )}.`,
        fix: "Filter by “Pets Allowed”, or remove the pets from your profile if you no longer keep them.",
      });
    } else if (isOn(flat.petAllowed)) {
      matches.push({ key: "pets", title: "Pets allowed" });
    }
  }

  // 4. Parking — owner's parkingAvailable vs tenant's vehicles.
  const vehicles = vehicleCount(tenantProfile);
  if (vehicles > 0) {
    if (isExplicitNo(flat.parkingAvailable)) {
      push({
        key: "parking",
        title: "No parking",
        reason: `This property has no parking, and your profile lists ${plural(
          vehicles,
          "vehicle"
        )}.`,
        fix: "Pick a property with parking, or update the vehicles on your profile.",
      });
    } else if (isOn(flat.parkingAvailable)) {
      matches.push({ key: "parking", title: "Parking available" });
    }
  }

  // 5. Occupancy — owner's personAllowed vs tenant's household size.
  const personAllowed = Number(flat.personAllowed) || 0;
  const familyMembers = Number(tenantProfile.totalFamilyMembers) || 0;
  if (personAllowed > 0 && familyMembers > 0) {
    if (familyMembers > personAllowed) {
      push({
        key: "occupancy",
        title: "Over occupancy limit",
        reason: `Owner allows a maximum of ${plural(
          personAllowed,
          "occupant"
        )}, and your profile lists ${plural(familyMembers, "member")}.`,
        fix: "Look for a property with a higher occupancy limit.",
      });
    } else {
      matches.push({
        key: "occupancy",
        title: `Fits ${plural(familyMembers, "occupant")}`,
      });
    }
  }

  const primaryReason = blockers[0]?.reason || "";

  return {
    canRequest: blockers.length === 0,
    blockers,
    warnings,
    matches,
    primaryReason,
    // Plain-text tooltip body — render inside an element with
    // `whiteSpace: "pre-line"` so the bullets keep their line breaks.
    tooltip: blockers.length
      ? [
          blockers.length === 1
            ? "You cannot send a request for this property:"
            : `You cannot send a request for this property (${blockers.length} reasons):`,
          ...blockers.map((rule) => `• ${rule.reason}`),
        ].join("\n")
      : "",
    shortLabel: blockers.length
      ? blockers.length === 1
        ? blockers[0].title
        : `${blockers.length} mismatches`
      : "",
    hasProfile,
  };
}

// The tenant profile lives in two places on the validateUser payload; both
// screens that need it were already merging them by hand.
export const tenantProfileFromUser = (validateUser) => ({
  ...validateUser?.UserValidate?.data?.user,
  ...validateUser?.UserValidate?.data?.tenantProfile,
});
